import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react'
import { createLead, type CreateLeadPayload } from '../lib/leads'
import { buildWhatsAppUrl, DEFAULT_WHATSAPP_MESSAGE } from '../lib/whatsapp'

type WhatsAppContextValue = {
  openWhatsApp: () => void
}

const WhatsAppContext = createContext<WhatsAppContextValue | null>(null)

export function useWhatsApp() {
  const ctx = useContext(WhatsAppContext)
  if (!ctx) {
    throw new Error('useWhatsApp must be used within WhatsAppProvider')
  }
  return ctx
}

const QUANTITY_OPTIONS = ['10', '20'] as const
const AUTO_SAVE_DELAY_MS = 1000

function isEmailValid(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

function buildLeadPayload(
  name: string,
  email: string,
  phone: string,
  finalQty: string,
): CreateLeadPayload | null {
  const nome = name.trim()
  const emailTrim = email.trim()
  const telefone = phone.trim()
  const qty = Number(finalQty)

  if (!nome || !isEmailValid(emailTrim) || !telefone || !qty || qty < 1) {
    return null
  }

  return {
    Nome: nome,
    Email: emailTrim,
    Telefone: telefone,
    QuantidadeObjetos: qty,
  }
}

function QuoteFormModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [quantity, setQuantity] = useState('')
  const [customQty, setCustomQty] = useState('')

  const isCustom = quantity === 'custom'
  const finalQty = isCustom ? customQty : quantity

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  useEffect(() => {
    const ping = () => {
      fetch('https://back-end-artcustom.onrender.com/api/auth/health').catch(() => {})
    }
    ping()
    const interval = setInterval(ping, 59_000)
    return () => clearInterval(interval)
  }, [])

  const [submitting, setSubmitting] = useState(false)
  const hasSavedRef = useRef(false)
  const saveInFlightRef = useRef(false)

  const payload = buildLeadPayload(name, email, phone, finalQty)
  const payloadKey = payload ? JSON.stringify(payload) : ''
  const isValid = payload !== null

  const persistLead = useCallback(async (data: CreateLeadPayload) => {
    if (hasSavedRef.current || saveInFlightRef.current) return

    saveInFlightRef.current = true
    try {
      await createLead(data)
      hasSavedRef.current = true
    } catch {
      // salvamento silencioso em segundo plano
    } finally {
      saveInFlightRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!payloadKey || hasSavedRef.current) return

    const timer = window.setTimeout(() => {
      const data = buildLeadPayload(name, email, phone, finalQty)
      if (data && !hasSavedRef.current) void persistLead(data)
    }, AUTO_SAVE_DELAY_MS)

    return () => window.clearTimeout(timer)
  }, [payloadKey, name, email, phone, finalQty, persistLead])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    if (payload) {
      await persistLead(payload)
    }

    window.open(buildWhatsAppUrl(DEFAULT_WHATSAPP_MESSAGE), '_blank', 'noopener,noreferrer')
    setSubmitting(false)
    onClose()
  }

  const inputClass =
    'w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 focus:border-[#0877c5] focus:ring-2 focus:ring-[#0877c5]/20 focus:bg-white outline-none transition-all'

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-md px-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="quote-form-title"
        className="w-full max-w-lg rounded-2xl overflow-hidden border border-[#0877c5]/15"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-2 bg-gradient-to-r from-[#0877c5] via-[#b51625] to-[#f3eb03]" />

        <div className="bg-white p-8">
          <p className="text-sm font-semibold tracking-widest uppercase text-[#0877c5] mb-3">
            Orçamento
          </p>
          <h2
            id="quote-form-title"
            className="text-2xl md:text-3xl font-bold mb-6 bg-gradient-to-r from-[#0877c5] via-[#b51625] to-[#f3eb03] bg-clip-text text-transparent"
          >
            Solicitar orçamento
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="quote-name" className="block text-sm font-medium text-gray-700 mb-1">
                Nome
              </label>
              <input
                id="quote-name"
                type="text"
                required
                placeholder="Seu nome completo"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="quote-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="quote-email"
                type="email"
                required
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="quote-phone" className="block text-sm font-medium text-gray-700 mb-1">
                Telefone
              </label>
              <input
                id="quote-phone"
                type="tel"
                required
                placeholder="(11) 99999-9999"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantidade de peças
              </label>
              <div className="flex gap-3">
                {QUANTITY_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => { setQuantity(opt); setCustomQty('') }}
                    className={`flex-1 rounded-xl border py-3 text-base font-semibold transition-all ${
                      quantity === opt
                        ? 'border-[#0877c5] bg-[#0877c5]/10 text-[#0877c5]'
                        : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setQuantity('custom')}
                  className={`flex-1 rounded-xl border py-3 text-base font-semibold transition-all ${
                    isCustom
                      ? 'border-[#0877c5] bg-[#0877c5]/10 text-[#0877c5]'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  Outro
                </button>
              </div>
              {isCustom && (
                <input
                  type="number"
                  min="1"
                  required
                  placeholder="Digite a quantidade"
                  value={customQty}
                  onChange={(e) => setCustomQty(e.target.value)}
                  className={`${inputClass} mt-3`}
                  autoFocus
                />
              )}
            </div>

            <div className="flex gap-4 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-xl border border-[#0877c5]/25 py-3.5 text-base font-medium text-[#0877c5] hover:bg-[#0877c5]/5 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!isValid || submitting}
                className="flex-1 rounded-xl bg-gradient-to-r from-[#0877c5] to-[#b51625] py-3.5 text-base font-semibold text-white hover:shadow-lg hover:opacity-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? 'Enviando...' : 'Enviar via WhatsApp'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export function WhatsAppProvider({ children }: { children: ReactNode }) {
  const [formOpen, setFormOpen] = useState(false)

  const openWhatsApp = useCallback(() => {
    setFormOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setFormOpen(false)
  }, [])

  return (
    <WhatsAppContext.Provider value={{ openWhatsApp }}>
      {children}
      {formOpen && <QuoteFormModal onClose={handleClose} />}
    </WhatsAppContext.Provider>
  )
}
